import React, { useEffect, useRef, useState } from "react";
import styled, { css } from "styled-components";
import { Container, Eyebrow, Display, Lead, PrimaryButton, Arrow, useReveal } from "../components/landing/ui";
import { colors, fonts, media, copperGradient } from "../styles/theme";
import { contact, links, loginPage } from "./landingContent";

// Web app sign-in. Accounts are issued by the team; there is no account service behind this
// form yet, so it checks nothing remotely and every attempt shows the incorrect-details
// notice. Credentials are never sent or stored.

const Wrap = styled.section`
  display: flex;
  align-items: center;
  min-height: 78vh;
  padding: 140px 0 120px;
`;

const Card = styled.div`
  max-width: 560px;
  margin: 0 auto;
  padding: 40px 28px;
  border-radius: 30px;
  background: ${colors.linen};
  border: 1px solid ${colors.stone};
  text-align: center;

  @media ${media.medium} {
    padding: 52px 56px;
  }
`;

const Form = styled.form`
  display: grid;
  gap: 16px;
  margin-top: 30px;
  text-align: left;
`;

const Field = styled.div`
  display: grid;
  gap: 8px;
`;

const Label = styled.label`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  font-family: ${fonts.body};
  font-size: 15px;
  font-weight: 600;
  color: ${colors.ink};

  a {
    font-weight: 400;
    font-size: 14px;
    color: ${colors.copper};
  }
`;

const inputStyles = css`
  width: 100%;
  padding: 14px 16px;
  border-radius: 14px;
  border: 1px solid ${(p) => (p.$invalid ? colors.terracotta : colors.stone)};
  background: ${colors.limestone};
  font-family: ${fonts.body};
  font-size: 16px;
  color: ${colors.ink};
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
  -webkit-appearance: none;
  appearance: none;

  &::placeholder {
    color: ${colors.pebble};
  }
  &:hover {
    border-color: ${(p) => (p.$invalid ? colors.terracotta : colors.pebble)};
  }
  &:focus {
    outline: none;
    background: ${colors.linen};
    border-color: ${colors.moss};
    box-shadow: 0 0 0 4px rgba(79, 100, 69, 0.15);
  }
`;

const Input = styled.input`
  ${inputStyles}
`;

// The incorrect-details notice: slides in above the button, announced to screen readers.
const Notice = styled.div`
  display: grid;
  gap: 4px;
  padding: 14px 16px;
  border-radius: 14px;
  border: 1px solid rgba(154, 78, 46, 0.35);
  background: rgba(154, 78, 46, 0.08);
  animation: notice-in 0.25s ease;

  b {
    font-family: ${fonts.body};
    font-size: 15px;
    font-weight: 600;
    color: ${colors.terracotta};
  }
  p {
    font-family: ${fonts.body};
    font-size: 14px;
    line-height: 1.5;
    color: ${colors.bark};
  }
  a {
    color: ${colors.copper};
    font-weight: 600;
  }

  @keyframes notice-in {
    from {
      opacity: 0;
      transform: translateY(-6px);
    }
    to {
      opacity: 1;
      transform: none;
    }
  }
`;

const Submit = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  height: 52px;
  margin-top: 4px;
  padding: 0 26px;
  border: 0;
  border-radius: 999px;
  background: ${copperGradient};
  font-family: ${fonts.body};
  font-size: 16px;
  font-weight: 600;
  color: ${colors.linen};
  cursor: pointer;
  box-shadow: 0 14px 30px -18px rgba(143, 83, 41, 0.8);
  transition: transform 0.25s ease, box-shadow 0.25s ease, opacity 0.25s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 18px 34px -18px rgba(143, 83, 41, 0.9);
  }
  &:disabled {
    opacity: 0.7;
    cursor: default;
    transform: none;
  }
`;

const Foot = styled.div`
  margin-top: 30px;
  padding-top: 26px;
  border-top: 1px solid ${colors.stone};

  p {
    font-family: ${fonts.body};
    font-size: 15px;
    line-height: 1.55;
    color: ${colors.bark};
  }
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
  margin-top: 18px;
`;

const Secondary = styled.a`
  display: inline-flex;
  align-items: center;
  height: 52px;
  padding: 0 24px;
  border-radius: 999px;
  border: 1px solid ${colors.stone};
  background: rgba(250, 247, 241, 0.8);
  font-family: ${fonts.body};
  font-size: 16px;
  font-weight: 600;
  color: ${colors.ink};
  transition: border-color 0.25s ease;

  &:hover {
    border-color: ${colors.copper};
  }
`;

const Login = () => {
  const scope = useRef(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  useReveal(scope);

  useEffect(() => {
    document.title = "vimbal – Log in";
  }, []);

  const submit = (e) => {
    e.preventDefault();
    if (busy) return;
    setFailed(false);
    setBusy(true);
    // No account service yet: a short pause, then the incorrect-details notice.
    setTimeout(() => {
      setBusy(false);
      setFailed(true);
      setPassword("");
    }, 700);
  };

  const mail = (subject) => `mailto:${contact.email}?subject=${encodeURIComponent(subject)}`;

  return (
    <Wrap ref={scope} aria-labelledby="login-title">
      <Container>
        <Card data-reveal>
          <Eyebrow $color={colors.ocean}>{loginPage.eyebrow}</Eyebrow>
          <Display id="login-title" style={{ marginTop: 18, fontSize: "clamp(34px, 3.6vw, 48px)" }}>
            {loginPage.titleLead}
            <br />
            <em>{loginPage.titleEmphasis}</em>
          </Display>
          <Lead style={{ marginTop: 16, marginLeft: "auto", marginRight: "auto" }}>{loginPage.body}</Lead>

          <Form onSubmit={submit} noValidate>
            <Field>
              <Label htmlFor="login-email">{loginPage.emailLabel}</Label>
              <Input
                id="login-email"
                name="email"
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                $invalid={failed}
                aria-invalid={failed || undefined}
              />
            </Field>
            <Field>
              <Label htmlFor="login-password">
                {loginPage.passwordLabel}
                <a href={mail(loginPage.forgotSubject)}>{loginPage.forgot}</a>
              </Label>
              <Input
                id="login-password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                $invalid={failed}
                aria-invalid={failed || undefined}
              />
            </Field>
            {failed && (
              <Notice role="alert">
                <b>{loginPage.errorTitle}</b>
                <p>{loginPage.errorBody}</p>
              </Notice>
            )}
            <Submit type="submit" disabled={busy}>
              {busy ? loginPage.submitting : loginPage.submit}
              {!busy && <Arrow />}
            </Submit>
          </Form>

          <Foot>
            <p>{loginPage.noAccount}</p>
            <Actions>
              <Secondary href={mail(loginPage.emailSubject)}>{loginPage.emailCta}</Secondary>
              <PrimaryButton to={links.register}>
                {loginPage.registerCta} <Arrow />
              </PrimaryButton>
            </Actions>
          </Foot>
        </Card>
      </Container>
    </Wrap>
  );
};

export default Login;
